import React, { useState, useEffect, useRef } from 'react';
import { Folder, FileText, Search, Grid, List, MoreVertical, Calendar, ChevronDown, File, FileSpreadsheet, Download, Trash2, Edit2, Eye, X, ChevronRight, Home, FolderOpen, ZoomIn, ZoomOut, Plus, Upload, FolderPlus, Loader2, Pencil, Check, HardDrive, Cloud } from 'lucide-react';
import { toast } from 'sonner';
import { useUserId } from '../context/UserAuthContext';
import { useDemoMode } from '../context/DemoContext';
import GoogleDocsSection from '../components/documents/GoogleDocsSection';
import GoogleDrivePanel from '../components/GoogleDrivePanel';

// API URL - dynamically determine at runtime based on window location
const API_URL = typeof window !== 'undefined' ? window.location.origin : '';
console.log('[Documents Page] Using runtime API_URL:', API_URL);

const RealEstateManagingPartnerDocuments = () => {
  const userId = useUserId();  // Get current user ID for data isolation
  const { isDemoMode, demoData, triggerViewOnlyWarning } = useDemoMode();
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list' - DEFAULT TO LIST
  
  useEffect(() => {
    console.log('ViewMode changed to:', viewMode);
  }, [viewMode]);
  const [selectedType, setSelectedType] = useState('All Type');
  const [dateRange, setDateRange] = useState('Last 30 Days');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [showAllFoldersModal, setShowAllFoldersModal] = useState(false);
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showMoveToFolderModal, setShowMoveToFolderModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Document upload state
  const [showAddDocumentModal, setShowAddDocumentModal] = useState(false);
  const [newDocument, setNewDocument] = useState({ name: '', category: 'Properties', type: 'PDF' });
  const [selectedUploadFile, setSelectedUploadFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Bulk upload — multi-file (PDF, CSV, images, etc.)
  const bulkInputRef = useRef(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ uploaded: 0, total: 0, failed: 0 });
  const fileInputRef = useRef(null);
  
  // Folder creation state
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderColor, setNewFolderColor] = useState('bg-blue-500');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  
  // Folder edit state
  const [showEditFolderModal, setShowEditFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState('');
  const [editFolderColor, setEditFolderColor] = useState('');
  const [editFolderCategory, setEditFolderCategory] = useState('');
  
  // Document edit state
  const [showEditDocumentModal, setShowEditDocumentModal] = useState(false);
  const [editingDocument, setEditingDocument] = useState(null);
  const [editDocumentName, setEditDocumentName] = useState('');
  const [editDocumentCategory, setEditDocumentCategory] = useState('');
  const [editDocumentNotes, setEditDocumentNotes] = useState('');
  
  // Data from API
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [userFolders, setUserFolders] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  
  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const [dropdownFile, setDropdownFile] = useState(null);

  // Calculate fixed position for dropdown based on button click
  const openDropdown = (e, file) => {
    const fileId = typeof file === 'string' ? file : file.id;
    if (openDropdownId === fileId) {
      setOpenDropdownId(null);
      setDropdownFile(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = 220;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < menuHeight
      ? rect.top - menuHeight
      : rect.bottom;
    setDropdownPos({ top, right: window.innerWidth - rect.right });
    setOpenDropdownId(fileId);
    setDropdownFile(typeof file === 'string' ? null : file);
  };
  
  // Filter states
  const [selectedFileType, setSelectedFileType] = useState('all');
  
  // Add to Meeting state
  const [showAddToMeetingModal, setShowAddToMeetingModal] = useState(false);
  const [documentToAddToMeeting, setDocumentToAddToMeeting] = useState(null);
  const [availableMeetings, setAvailableMeetings] = useState([]);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);
  const [isAddingToMeeting, setIsAddingToMeeting] = useState(false);
  const [meetingSearchQuery, setMeetingSearchQuery] = useState('');
  
  // Document source: 'local' or 'google'
  const [documentSource, setDocumentSource] = useState('local');

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (openDropdownId) {
        setOpenDropdownId(null);
        setDropdownFile(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdownId]);

  // Fetch documents and folders on mount
  useEffect(() => {
    // Use demo data if in demo mode
    if (isDemoMode && demoData?.documents) {
      const demoDocs = demoData.documents.map(doc => ({
        id: doc.id,
        original_name: doc.name,
        file_type: doc.type || 'document',
        file_size: doc.size || '1.0 MB',
        created_at: doc.created_at,
        status: doc.status || 'Active',
        folder_id: null
      }));
      setUploadedDocuments(demoDocs);
      setUserFolders([
        { id: 'demo-folder-1', name: 'Contracts', color: 'bg-blue-500' },
        { id: 'demo-folder-2', name: 'Reports', color: 'bg-green-500' },
        { id: 'demo-folder-3', name: 'Invoices', color: 'bg-yellow-500' }
      ]);
      setIsLoadingData(false);
      console.log(`📄 Demo mode: Loaded ${demoDocs.length} demo documents`);
      return;
    }
    
    if (userId) {
      fetchDocuments();
      fetchFolders();
    } else {
      setUploadedDocuments([]);
      setUserFolders([]);
      setIsLoadingData(false);
    }
  }, [userId, isDemoMode, demoData]);

  const fetchDocuments = async () => {
    if (!userId) {
      setUploadedDocuments([]);
      setIsLoadingData(false);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/documents-storage/documents?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUploadedDocuments(data.documents || []);
      } else {
        setUploadedDocuments([]);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
      setUploadedDocuments([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  const fetchFolders = async () => {
    if (!userId) {
      setUserFolders([]);
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/documents-storage/folders?user_id=${userId}`);
      if (response.ok) {
        const data = await response.json();
        setUserFolders(data.folders || []);
      } else {
        setUserFolders([]);
      }
    } catch (error) {
      console.error('Error fetching folders:', error);
      setUserFolders([]);
    }
  };

  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedUploadFile(file);
      // Auto-fill name if empty
      if (!newDocument.name) {
        const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
        setNewDocument(prev => ({ ...prev, name: nameWithoutExt }));
      }
    }
  };

  // Handle document upload
  const handleUploadDocument = async () => {
    if (!selectedUploadFile || !newDocument.name) {
      alert('Please select a file and enter a name');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedUploadFile);
      formData.append('name', newDocument.name);
      formData.append('user_id', userId);  // CRITICAL: Include user_id for data isolation
      formData.append('category', newDocument.category);
      if (currentFolder) {
        formData.append('folder_id', currentFolder.id);
      }

      const response = await fetch(`${API_URL}/api/documents-storage/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const uploadedDoc = await response.json();
        setUploadedDocuments(prev => [uploadedDoc, ...prev]);
        setShowAddDocumentModal(false);
        setNewDocument({ name: '', category: 'Properties', type: 'PDF' });
        setSelectedUploadFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        const error = await response.json();
        alert(`Upload failed: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  // Bulk upload handler — accepts ANY number of files of any type at once
  const handleBulkFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !userId) return;
    setBulkUploading(true);
    setBulkProgress({ uploaded: 0, total: files.length, failed: 0 });

    const fd = new FormData();
    fd.append('user_id', userId);
    fd.append('category', currentFolder?.category || 'Properties');
    if (currentFolder?.id) fd.append('folder_id', currentFolder.id);
    files.forEach(f => fd.append('files', f));

    try {
      const r = await fetch(`${API_URL}/api/documents-storage/upload-bulk`, { method: 'POST', body: fd });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Bulk upload failed');

      setBulkProgress({
        uploaded: data.uploaded || 0,
        failed: data.failed || 0,
        total: data.total || files.length,
      });

      const newDocs = (data.results || []).filter(r => r.ok).map(r => ({
        id: r.id,
        name: r.filename,
        type: r.file_type,
        category: currentFolder?.category || 'Properties',
        folder_id: currentFolder?.id || null,
        size: r.size,
        uploaded_at: new Date().toISOString(),
      }));
      if (newDocs.length) setUploadedDocuments(prev => [...newDocs, ...prev]);

      if (data.failed > 0) {
        const fails = (data.results || []).filter(r => !r.ok).map(r => r.filename).join(', ');
        toast.error(`Uploaded ${data.uploaded}/${data.total}. Failed: ${fails}`);
      } else {
        toast.success(`Uploaded ${data.uploaded} file${data.uploaded !== 1 ? 's' : ''}`);
      }
    } catch (err) {
      console.error('Bulk upload error:', err);
      toast.error(`Bulk upload failed: ${err.message}`);
    } finally {
      setBulkUploading(false);
      if (bulkInputRef.current) bulkInputRef.current.value = '';
      setTimeout(() => setBulkProgress({ uploaded: 0, total: 0, failed: 0 }), 4000);
    }
  };

  // Handle folder creation
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      alert('Please enter a folder name');
      return;
    }

    setIsCreatingFolder(true);
    try {
      const response = await fetch(`${API_URL}/api/documents-storage/folders?user_id=${userId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newFolderName,
          parent_id: currentFolder?.id || null,
          color: newFolderColor,
        }),
      });

      if (response.ok) {
        const newFolder = await response.json();
        setUserFolders(prev => [...prev, newFolder]);
        setShowAddFolderModal(false);
        setNewFolderName('');
        setNewFolderColor('bg-blue-500');
      } else {
        const error = await response.json();
        alert(`Failed to create folder: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Create folder error:', error);
      alert('Failed to create folder');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  // Handle document deletion
  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/api/documents-storage/documents/${docId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setUploadedDocuments(prev => prev.filter(d => d.id !== docId));
        toast.success('Document deleted');
      }
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    }
  };

  // Handle opening document edit modal
  const handleEditDocument = (doc) => {
    setEditingDocument(doc);
    setEditDocumentName(doc.name || doc.filename || '');
    setEditDocumentCategory(doc.category || 'Properties');
    setEditDocumentNotes(doc.notes || '');
    setShowEditDocumentModal(true);
    setOpenDropdownId(null);
  };

  // Handle saving document edits
  const handleSaveDocumentEdit = async () => {
    if (!editingDocument) return;

    try {
      const response = await fetch(`${API_URL}/api/documents-storage/documents/${editingDocument.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editDocumentName,
          category: editDocumentCategory,
          notes: editDocumentNotes,
        }),
      });

      if (response.ok) {
        const updatedDoc = await response.json();
        setUploadedDocuments(prev => prev.map(d => 
          d.id === editingDocument.id 
            ? { ...d, name: editDocumentName, category: editDocumentCategory, notes: editDocumentNotes }
            : d
        ));
        setShowEditDocumentModal(false);
        setEditingDocument(null);
        toast.success('Document updated');
      } else {
        toast.error('Failed to update document');
      }
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update document');
    }
  };

  // Handle folder deletion
  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm('Are you sure you want to delete this folder and all its contents?')) return;

    try {
      const response = await fetch(`${API_URL}/api/documents-storage/folders/${folderId}?user_id=${userId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setUserFolders(prev => prev.filter(f => f.id !== folderId));
        // Also remove documents in that folder from local state
        setUploadedDocuments(prev => prev.filter(d => d.folder_id !== folderId));
      }
    } catch (error) {
      console.error('Delete folder error:', error);
    }
  };

  // Handle moving files to a folder
  const handleMoveFilesToFolder = async (targetFolderId) => {
    if (selectedItems.length === 0) return;
    
    try {
      // Move each selected file to the target folder
      const movePromises = selectedItems.map(async (fileId) => {
        const response = await fetch(`${API_URL}/api/documents-storage/documents/${fileId}/move`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folder_id: targetFolderId }),
        });
        return response.ok;
      });
      
      await Promise.all(movePromises);
      
      // Update local state
      setUploadedDocuments(prev => prev.map(doc => 
        selectedItems.includes(doc.id) 
          ? { ...doc, folder_id: targetFolderId }
          : doc
      ));
      
      // Clear selection and close modal
      setSelectedItems([]);
      setShowMoveToFolderModal(false);
      
      alert(`${selectedItems.length} file(s) moved successfully!`);
    } catch (error) {
      console.error('Move files error:', error);
      alert('Failed to move files');
    }
  };

  // Open edit folder modal
  const openEditFolderModal = (folder) => {
    setEditingFolder(folder);
    setEditFolderName(folder.name);
    setEditFolderColor(folder.color || 'bg-blue-500');
    setEditFolderCategory(folder.category || 'General');
    setShowEditFolderModal(true);
  };

  // Handle folder update
  const handleUpdateFolder = async () => {
    if (!editingFolder || !editFolderName.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/documents-storage/folders/${editingFolder.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editFolderName,
          color: editFolderColor,
          category: editFolderCategory
        })
      });

      if (response.ok) {
        setUserFolders(prev => prev.map(f => 
          f.id === editingFolder.id 
            ? { ...f, name: editFolderName, color: editFolderColor, category: editFolderCategory }
            : f
        ));
        setShowEditFolderModal(false);
        setEditingFolder(null);
      }
    } catch (error) {
      console.error('Update folder error:', error);
    }
  };

  // Fetch available meetings for "Add to Meeting" feature
  const fetchMeetings = async () => {
    setIsLoadingMeetings(true);
    try {
      // Fetch from both regular meetings and excel meetings
      const [meetingsRes, excelRes] = await Promise.all([
        fetch(`${API_URL}/api/meetings/`),
        fetch(`${API_URL}/api/excel/get-desktop-data`)
      ]);
      
      let allMeetings = [];
      
      if (meetingsRes.ok) {
        const meetings = await meetingsRes.json();
        allMeetings = [...allMeetings, ...meetings.map(m => ({ ...m, source: 'meetings' }))];
      }
      
      if (excelRes.ok) {
        const excelData = await excelRes.json();
        const excelMeetings = excelData.meetings || [];
        allMeetings = [...allMeetings, ...excelMeetings.map(m => ({ ...m, source: 'excel' }))];
      }
      
      // Sort by date (most recent first)
      allMeetings.sort((a, b) => new Date(b.datetime || b.date) - new Date(a.datetime || a.date));
      
      setAvailableMeetings(allMeetings);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    } finally {
      setIsLoadingMeetings(false);
    }
  };

  // Open Add to Meeting modal
  const openAddToMeetingModal = (document) => {
    setDocumentToAddToMeeting(document);
    setShowAddToMeetingModal(true);
    setMeetingSearchQuery('');
    fetchMeetings();
  };

  // Add document to selected meeting
  const handleAddDocumentToMeeting = async (meeting) => {
    if (!documentToAddToMeeting || !meeting) return;
    
    setIsAddingToMeeting(true);
    try {
      // Prepare document data
      const docData = {
        filename: documentToAddToMeeting.id + '.' + (documentToAddToMeeting.type?.toLowerCase() || 'pdf'),
        original_filename: documentToAddToMeeting.name,
        url: documentToAddToMeeting.file_path || documentToAddToMeeting.download_url || `/api/documents-storage/documents/${documentToAddToMeeting.id}/download`,
        content_type: documentToAddToMeeting.content_type || getContentType(documentToAddToMeeting.type),
        size: documentToAddToMeeting.size || 'Unknown'
      };
      
      const response = await fetch(`${API_URL}/api/meetings/${meeting.id}/documents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(docData)
      });
      
      if (response.ok) {
        alert(`Document "${documentToAddToMeeting.name}" added to meeting "${meeting.title}"!`);
        setShowAddToMeetingModal(false);
        setDocumentToAddToMeeting(null);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to add document to meeting');
      }
    } catch (error) {
      console.error('Error adding document to meeting:', error);
      alert('Failed to add document to meeting: ' + error.message);
    } finally {
      setIsAddingToMeeting(false);
    }
  };

  // Get content type from file type
  const getContentType = (type) => {
    const types = {
      'PDF': 'application/pdf',
      'DOC': 'application/msword',
      'DOCX': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'XLS': 'application/vnd.ms-excel',
      'XLSX': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'PPT': 'application/vnd.ms-powerpoint',
      'PPTX': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'TXT': 'text/plain',
      'PNG': 'image/png',
      'JPG': 'image/jpeg',
      'JPEG': 'image/jpeg'
    };
    return types[type?.toUpperCase()] || 'application/octet-stream';
  };

  // Filter meetings by search query
  const filteredMeetings = availableMeetings.filter(meeting => {
    if (!meetingSearchQuery) return true;
    const query = meetingSearchQuery.toLowerCase();
    return (
      meeting.title?.toLowerCase().includes(query) ||
      meeting.organization?.toLowerCase().includes(query) ||
      meeting.participants?.some(p => p.name?.toLowerCase().includes(query))
    );
  });

  // Folder color options with matching categories
  const folderColorOptions = [
    { color: 'bg-purple-500', category: 'Properties', label: 'Purple' },
    { color: 'bg-blue-500', category: 'Legal', label: 'Blue' },
    { color: 'bg-green-500', category: 'Finance', label: 'Green' },
    { color: 'bg-pink-500', category: 'Marketing', label: 'Pink' },
    { color: 'bg-orange-500', category: 'Tax', label: 'Orange' },
    { color: 'bg-cyan-500', category: 'Compliance', label: 'Cyan' },
    { color: 'bg-yellow-500', category: 'Investment', label: 'Yellow' },
    { color: 'bg-gray-500', category: 'General', label: 'Gray' },
  ];

  // Define default document categories
  const defaultDocCategories = [
    { id: 'all', name: 'All', color: 'bg-white', textColor: 'text-black', icon: '📁', order: 0 },
    { id: 'Properties', name: 'Properties', color: 'bg-purple-600', textColor: 'text-white', icon: '🏠', order: 1 },
    { id: 'Legal', name: 'Legal', color: 'bg-blue-600', textColor: 'text-white', icon: '⚖️', order: 2 },
    { id: 'Finance', name: 'Finance', color: 'bg-green-600', textColor: 'text-white', icon: '💰', order: 3 },
    { id: 'Marketing', name: 'Marketing', color: 'bg-pink-600', textColor: 'text-white', icon: '📣', order: 4 },
    { id: 'Development', name: 'Development', color: 'bg-purple-600', textColor: 'text-white', icon: '🏗️', order: 5 },
    { id: 'Tax', name: 'Tax', color: 'bg-orange-600', textColor: 'text-white', icon: '📊', order: 6 },
    { id: 'Appraisal', name: 'Appraisal', color: 'bg-indigo-600', textColor: 'text-white', icon: '📋', order: 7 },
    { id: 'Leasing', name: 'Leasing', color: 'bg-teal-600', textColor: 'text-white', icon: '🔑', order: 8 },
    { id: 'Investment', name: 'Investment', color: 'bg-yellow-600', textColor: 'text-white', icon: '📈', order: 9 },
    { id: 'Compliance', name: 'Compliance', color: 'bg-cyan-600', textColor: 'text-white', icon: '✅', order: 10 }
  ];
  
  // Categories from API (persistent)
  const [customDocCategories, setCustomDocCategories] = useState(defaultDocCategories);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  
  // Fetch categories from API
  const fetchCategories = async () => {
    if (!userId) return;
    try {
      setIsLoadingCategories(true);
      const response = await fetch(`${API_URL}/api/document-categories?user_id=${encodeURIComponent(userId)}`);
      if (response.ok) {
        const data = await response.json();
        if (data.categories && data.categories.length > 0) {
          setCustomDocCategories(data.categories);
        }
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setIsLoadingCategories(false);
    }
  };
  
  // Load categories on mount
  useEffect(() => {
    if (userId) {
      fetchCategories();
    }
  }, [userId]);
  
  // Editable category state
  const [showDocCategoryModal, setShowDocCategoryModal] = useState(false);
  const [newDocCategoryName, setNewDocCategoryName] = useState('');
  const [newDocCategoryIcon, setNewDocCategoryIcon] = useState('📄');
  const [newDocCategoryColor, setNewDocCategoryColor] = useState('bg-blue-600');
  const [editingDocCategory, setEditingDocCategory] = useState(null);
  const [editDocCategoryValue, setEditDocCategoryValue] = useState('');
  const [draggedCategoryId, setDraggedCategoryId] = useState(null);
  
  const docCategoryIcons = ['📁', '🏠', '⚖️', '💰', '📣', '🏗️', '📊', '📋', '🔑', '📈', '✅', '📄', '📝', '🗂️', '📂', '💼', '🏢', '📑', '🧾', '📃'];
  const docCategoryColors = ['bg-white', 'bg-purple-600', 'bg-blue-600', 'bg-green-600', 'bg-pink-600', 'bg-orange-600', 'bg-indigo-600', 'bg-teal-600', 'bg-yellow-600', 'bg-cyan-600', 'bg-red-600', 'bg-gray-600'];
  
  // Add category via API
  const addDocCategory = async () => {
    if (!newDocCategoryName.trim()) return;
    if (customDocCategories.some(c => c.id === newDocCategoryName)) { 
      toast.error('Category already exists'); 
      return; 
    }
    
    try {
      const response = await fetch(`${API_URL}/api/document-categories?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: newDocCategoryName,
          color: newDocCategoryColor,
          textColor: 'text-white',
          icon: newDocCategoryIcon
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCustomDocCategories(prev => [...prev, data.category]);
        setNewDocCategoryName('');
        setNewDocCategoryIcon('📄');
        setNewDocCategoryColor('bg-blue-600');
        toast.success(`Added "${newDocCategoryName}" category`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to add category');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      toast.error('Failed to add category');
    }
  };
  
  // Update category via API
  const updateDocCategory = async (oldId, newName) => {
    if (!newName.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/document-categories/${encodeURIComponent(oldId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName })
      });
      
      if (response.ok) {
        setCustomDocCategories(prev => prev.map(c => 
          c.id === oldId ? { ...c, id: newName, name: newName } : c
        ));
        if (selectedCategory === oldId) setSelectedCategory(newName);
        setEditingDocCategory(null);
        setEditDocCategoryValue('');
        toast.success(`Category renamed to "${newName}"`);
      } else {
        toast.error('Failed to update category');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };
  
  // Delete category via API
  const deleteDocCategory = async (categoryId) => {
    if (['all'].includes(categoryId)) { 
      toast.error('Cannot delete "All" category'); 
      return; 
    }
    
    if (!window.confirm(`Delete "${categoryId}" category? Documents in this category will not be deleted.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/document-categories/${encodeURIComponent(categoryId)}?user_id=${encodeURIComponent(userId)}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setCustomDocCategories(prev => prev.filter(c => c.id !== categoryId));
        if (selectedCategory === categoryId) setSelectedCategory('all');
        toast.success('Category deleted');
      } else {
        toast.error('Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    }
  };
  
  // Reorder categories via API (drag and drop)
  const handleCategoryDragStart = (e, categoryId) => {
    setDraggedCategoryId(categoryId);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleCategoryDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleCategoryDrop = async (e, targetCategoryId) => {
    e.preventDefault();
    if (!draggedCategoryId || draggedCategoryId === targetCategoryId) {
      setDraggedCategoryId(null);
      return;
    }
    
    // Reorder locally first
    const categories = [...customDocCategories];
    const draggedIndex = categories.findIndex(c => c.id === draggedCategoryId);
    const targetIndex = categories.findIndex(c => c.id === targetCategoryId);
    
    if (draggedIndex === -1 || targetIndex === -1) {
      setDraggedCategoryId(null);
      return;
    }
    
    const [draggedItem] = categories.splice(draggedIndex, 1);
    categories.splice(targetIndex, 0, draggedItem);
    
    // Update order
    const reorderedCategories = categories.map((c, i) => ({ ...c, order: i }));
    setCustomDocCategories(reorderedCategories);
    setDraggedCategoryId(null);
    
    // Save to API
    try {
      await fetch(`${API_URL}/api/document-categories/reorder?user_id=${encodeURIComponent(userId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category_ids: reorderedCategories.map(c => c.id) })
      });
      toast.success('Categories reordered');
    } catch (error) {
      console.error('Error reordering categories:', error);
    }
  };

  // Use custom categories
  const categories = customDocCategories;

  // Mock folders data with categories and contents
  // Empty static folders - all folders will come from API
  const folders = [];

  // Empty files - all files will come from API
  const [allFiles, setAllFiles] = useState([]);

  const getFileIcon = (type) => {
    switch (type) {
      case 'PDF':
        return { icon: File, color: 'bg-red-500' };
      case 'DOC':
        return { icon: FileText, color: 'bg-blue-500' };
      case 'XLS':
        return { icon: FileSpreadsheet, color: 'bg-green-500' };
      default:
        return { icon: File, color: 'bg-gray-500' };
    }
  };

  // Convert bg-* color to text-* color for folder icons
  const getFolderIconColor = (bgColor) => {
    if (!bgColor) return 'text-yellow-500';
    // Convert bg-color-shade to text-color-shade
    return bgColor.replace('bg-', 'text-');
  };

  // Get category from folder color (reverse lookup)
  const getCategoryFromColor = (bgColor) => {
    const colorToCategory = {
      'bg-purple-500': 'Properties',
      'bg-blue-500': 'Legal',
      'bg-green-500': 'Finance',
      'bg-pink-500': 'Marketing',
      'bg-orange-500': 'Tax',
      'bg-cyan-500': 'Compliance',
      'bg-yellow-500': 'Investment',
      'bg-gray-500': 'General',
      'bg-red-500': 'Development',
      'bg-teal-500': 'Leasing',
      'bg-indigo-500': 'Appraisal',
    };
    return colorToCategory[bgColor] || 'General';
  };

  // Get category color based on category name
  const getCategoryColor = (categoryName) => {
    const categoryColors = {
      'Properties': 'bg-purple-600',
      'Legal': 'bg-blue-600',
      'Finance': 'bg-green-600',
      'Marketing': 'bg-pink-600',
      'Development': 'bg-purple-600',
      'Tax': 'bg-orange-600',
      'Appraisal': 'bg-indigo-600',
      'Leasing': 'bg-teal-600',
      'Investment': 'bg-yellow-600',
      'Compliance': 'bg-cyan-600',
      'General': 'bg-gray-500',
      'Test': 'bg-emerald-600',
      'Uploaded via AI Assistant': 'bg-violet-600'
    };
    return categoryColors[categoryName] || 'bg-gray-500';
  };

  const toggleSelection = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  // Drag and drop handlers
  const handleDragStart = (e, item) => {
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, folderId = null) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (folderId && draggedItem) {
      setDragOverFolderId(folderId);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverFolderId(null);
  };

  const handleDrop = async (e, targetFolder) => {
    e.preventDefault();
    setDragOverFolderId(null);
    if (draggedItem && (draggedItem.type || draggedItem.file_type)) {
      // Moving a file into a folder via API
      try {
        const response = await fetch(`${API_URL}/api/documents-storage/documents/${draggedItem.id}/move`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ folder_id: targetFolder.id }),
        });
        
        if (response.ok) {
          // Update local state for uploaded documents
          setUploadedDocuments(prev => prev.map(doc => 
            doc.id === draggedItem.id 
              ? { ...doc, folder_id: targetFolder.id }
              : doc
          ));
          // Also update allFiles for static files
          setAllFiles(prev => prev.map(file => 
            file.id === draggedItem.id 
              ? { ...file, folderId: targetFolder.id, folder_id: targetFolder.id }
              : file
          ));
          alert(`"${draggedItem.name}" moved to "${targetFolder.name}"`);
        } else {
          alert('Failed to move file');
        }
      } catch (error) {
        console.error('Error moving file:', error);
        alert('Failed to move file');
      }
    }
    setDraggedItem(null);
  };

  // Navigation functions
  const openFolder = (folder) => {
    setCurrentFolder(folder);
    setSearchQuery('');
  };

  const goToRoot = () => {
    setCurrentFolder(null);
  };

  const openFile = (file) => {
    setSelectedFile(file);
    setShowFileViewer(true);
  };

  // Get files to display based on current location
  const getDisplayFiles = () => {
    // Combine static files with uploaded documents
    const combinedFiles = [...allFiles, ...uploadedDocuments];
    
    if (currentFolder) {
      // Inside a folder - show folder's files
      return combinedFiles.filter(file => file.folder_id === currentFolder.id || file.folderId === currentFolder.id);
    } else {
      // Root level - show only files not in folders
      return combinedFiles.filter(file => !file.folderId && !file.folder_id);
    }
  };

  // Filter by search query
  const searchFilteredFiles = getDisplayFiles().filter(file => 
    searchQuery === '' || file.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter by file type
  const typeFilteredFiles = selectedFileType === 'all'
    ? searchFilteredFiles
    : searchFilteredFiles.filter(file => {
        const fileType = (file.type || file.file_type || '').toUpperCase();
        if (selectedFileType === 'PDF') return fileType.includes('PDF');
        if (selectedFileType === 'DOC') return fileType.includes('DOC') || fileType.includes('DOCX');
        if (selectedFileType === 'XLS') return fileType.includes('XLS') || fileType.includes('XLSX');
        if (selectedFileType === 'IMG') return fileType.includes('PNG') || fileType.includes('JPG') || fileType.includes('JPEG') || fileType.includes('IMAGE');
        return true;
      });

  // Filter by date range
  const dateFilteredFiles = typeFilteredFiles.filter(file => {
    if (dateRange === 'All Time') return true;
    
    const fileDate = new Date(file.date || file.created_at);
    const now = new Date();
    const daysDiff = Math.floor((now - fileDate) / (1000 * 60 * 60 * 24));
    
    if (dateRange === 'Last 7 Days') return daysDiff <= 7;
    if (dateRange === 'Last 30 Days') return daysDiff <= 30;
    if (dateRange === 'Last 90 Days') return daysDiff <= 90;
    return true;
  });

  // Filter by category
  const filteredFiles = selectedCategory === 'all' 
    ? dateFilteredFiles 
    : dateFilteredFiles.filter(file => file.category === selectedCategory);

  // Combine static folders with user-created folders
  const allFoldersDisplay = [...folders, ...userFolders];
  
  // Filter folders by category (only at root level)
  const filteredFolders = selectedCategory === 'all'
    ? allFoldersDisplay
    : allFoldersDisplay.filter(folder => folder.category === selectedCategory);

  const totalCount = currentFolder ? filteredFiles.length : filteredFolders.length + filteredFiles.length;

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            {/* Breadcrumb Navigation */}
            <div className="flex items-center gap-2 mb-2">
              <button 
                onClick={goToRoot}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <Home size={16} />
              </button>
              {currentFolder && (
                <>
                  <ChevronRight size={14} className="text-gray-600" />
                  <button 
                    onClick={goToRoot}
                    className="text-gray-400 hover:text-white transition-colors text-xs md:text-sm"
                  >
                    Documents
                  </button>
                  <ChevronRight size={14} className="text-gray-600" />
                  <span className="text-white text-xs md:text-sm font-semibold truncate max-w-[120px] md:max-w-none">{currentFolder.name}</span>
                </>
              )}
            </div>
            <h1 className="text-xl md:text-3xl font-bold text-white">Files ({totalCount})</h1>
            <p className="text-gray-400 text-xs md:text-sm mt-1">
              {currentFolder ? `Viewing contents of ${currentFolder.name}` : 'Document Management System'}
            </p>
          </div>
          
          {/* View Toggle and Add Buttons */}
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            {/* Add Folder Button */}
            <button
              onClick={() => setShowAddFolderModal(true)}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 rounded-lg transition-all font-semibold text-xs md:text-sm"
            >
              <FolderPlus size={16} />
              <span className="hidden sm:inline">Add Folder</span>
              <span className="sm:hidden">Folder</span>
            </button>

            {/* Bulk Upload Button — multi-select any file type */}
            {bulkProgress.total > 0 && (
              <span className="text-xs text-emerald-300 bg-emerald-900/40 border border-emerald-500/30 rounded-md px-2 py-1">
                {bulkProgress.uploaded}/{bulkProgress.total}
                {bulkProgress.failed > 0 && ` · ${bulkProgress.failed} failed`}
              </span>
            )}
            <input
              ref={bulkInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleBulkFiles}
              data-testid="documents-re-bulk-upload-input"
            />
            <button
              onClick={() => bulkInputRef.current?.click()}
              disabled={bulkUploading}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg transition-all font-semibold text-xs md:text-sm"
              data-testid="documents-re-bulk-upload-btn"
              title="Upload multiple files at once (PDF, CSV, images, etc.)"
            >
              {bulkUploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
              <span className="hidden sm:inline">{bulkUploading ? 'Uploading…' : 'Bulk Upload'}</span>
              <span className="sm:hidden">Bulk</span>
            </button>

            {/* Add Document Button */}
            <button
              onClick={() => setShowAddDocumentModal(true)}
              className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-lg transition-all transform hover:scale-105 font-semibold text-xs md:text-sm"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Add Document</span>
              <span className="sm:hidden">Doc</span>
            </button>
            
            {/* View Toggle */}
            <button
              onClick={() => {
                console.log('Switching to grid view');
                setViewMode('grid');
              }}
              data-view="grid"
              className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                viewMode === 'grid' ? 'bg-white text-black' : 'bg-black border border-white text-white hover:bg-white/10'
              }`}
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => {
                console.log('Switching to list view');
                setViewMode('list');
              }}
              data-view="list"
              className={`p-1.5 md:p-2 rounded-lg transition-colors ${
                viewMode === 'list' ? 'bg-white text-black' : 'bg-black border border-white text-white hover:bg-white/10'
              }`}
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Document Source Tabs */}
        <div className="flex items-center gap-2 mt-4 border-b border-gray-800 pb-4">
          <button
            onClick={() => setDocumentSource('local')}
            data-testid="doc-source-local"
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              documentSource === 'local' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            <HardDrive size={16} />
            Local Library
          </button>
          <button
            onClick={() => setDocumentSource('dropbox')}
            data-testid="doc-source-dropbox"
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              documentSource === 'dropbox' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6 2L0 6l6 4-6 4 6 4 6-4-6-4 6-4-6-4z"/>
              <path d="M18 2l-6 4 6 4-6 4 6 4 6-4-6-4 6-4-6-4z"/>
              <path d="M12 10l-6 4 6 4 6-4-6-4z"/>
            </svg>
            Dropbox
          </button>
          <button
            onClick={() => setDocumentSource('google')}
            data-testid="doc-source-google"
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              documentSource === 'google' 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            <Cloud size={16} />
            Google Drive
          </button>
          <button
            onClick={() => setDocumentSource('mac')}
            data-testid="doc-source-mac"
            className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
              documentSource === 'mac' 
                ? 'bg-purple-600 text-white' 
                : 'bg-gray-900 text-gray-400 hover:bg-gray-800 hover:text-white'
            }`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.71 19.5C17.88 20.74 17 21.95 15.66 21.97C14.32 22 13.89 21.18 12.37 21.18C10.84 21.18 10.37 21.95 9.1 22C7.79 22.05 6.8 20.68 5.96 19.47C4.25 17 2.94 12.45 4.7 9.39C5.57 7.87 7.13 6.91 8.82 6.88C10.1 6.86 11.32 7.75 12.11 7.75C12.89 7.75 14.37 6.68 15.92 6.84C16.57 6.87 18.39 7.1 19.56 8.82C19.47 8.88 17.39 10.1 17.41 12.63C17.44 15.65 20.06 16.66 20.09 16.67C20.06 16.74 19.67 18.11 18.71 19.5ZM13 3.5C13.73 2.67 14.94 2.04 15.94 2C16.07 3.17 15.6 4.35 14.9 5.19C14.21 6.04 13.07 6.7 11.95 6.61C11.8 5.46 12.36 4.26 13 3.5Z"/>
            </svg>
            Mac Library
          </button>
        </div>

        {/* Google Drive — full browse / upload / spreadsheet-edit experience */}
        {documentSource === 'google' ? (
          <div className="mt-4">
            <GoogleDrivePanel userId={userId} />
          </div>
        ) : null}

        {/* Show Dropbox Connection UI when selected */}
        {documentSource === 'dropbox' && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-24 h-24 mb-6 flex items-center justify-center">
              <svg viewBox="0 0 256 218" className="w-full h-full">
                <path fill="#0061FF" d="M63.995 0L0 40.771l63.995 40.772 64.005-40.772zM191.99 0l-63.995 40.771 63.995 40.772 64.005-40.772zM0 122.314l63.995 40.771 64.005-40.771-64.005-40.772zM191.99 81.543l-63.995 40.771 63.995 40.772 64.005-40.772zM64.005 176.771L128.01 217.5l63.995-40.729-63.995-40.772z"/>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Connect Your Dropbox</h2>
            <p className="text-gray-400 text-center max-w-md mb-6">
              Stream photos and videos directly from your Dropbox account without downloading.
            </p>
            <button 
              onClick={() => toast.info('Dropbox integration coming soon')}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
              </svg>
              Connect Dropbox
            </button>
            <p className="text-gray-500 text-sm mt-4">
              You'll need to create a Dropbox App first at dropbox.com/developers
            </p>
          </div>
        )}

        {/* Show Mac Library UI when selected */}
        {documentSource === 'mac' && (
          <div className="space-y-4">
            {/* Mac Library Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors">
                  Setup Sync
                </button>
                <button className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors">
                  Refresh
                </button>
              </div>
            </div>
            
            {/* Mac Library Filters */}
            <div className="flex items-center gap-4 bg-gray-900 border border-gray-800 rounded-xl p-3">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input 
                  type="text" 
                  placeholder="Search photos..."
                  className="w-full pl-10 pr-4 py-2 bg-black border border-gray-700 rounded-lg text-white text-sm"
                />
              </div>
              <button className="px-3 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm">All (0)</button>
              <button className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm">All</button>
              <button className="px-3 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm">Photos</button>
              <button className="px-3 py-2 bg-gray-800 text-gray-400 rounded-lg text-sm">Videos</button>
              <div className="flex items-center gap-1 border-l border-gray-700 pl-4">
                <button className="p-2 bg-gray-800 text-white rounded-lg"><Grid size={16} /></button>
                <button className="p-2 bg-gray-800 text-gray-400 rounded-lg"><List size={16} /></button>
              </div>
              <select className="px-3 py-2 bg-gray-800 text-white rounded-lg text-sm border-0">
                <option>Newest First</option>
                <option>Oldest First</option>
              </select>
            </div>

            {/* Mac Library Empty State */}
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-20 h-20 mb-6 flex items-center justify-center bg-gray-800 rounded-xl">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-gray-500">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                  <circle cx="8.5" cy="8.5" r="1.5"/>
                  <path d="M21 15l-5-5L5 21"/>
                </svg>
              </div>
              <h2 className="text-xl font-bold text-white mb-2">No Photos Yet</h2>
              <p className="text-gray-400 text-center max-w-md mb-6">
                Sync your Mac Photos library to see your photos here. Click 'Setup Sync' to get started.
              </p>
              <button 
                onClick={() => toast.info('Mac Library sync setup coming soon')}
                className="flex items-center gap-2 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors"
              >
                Setup Sync Script
              </button>
            </div>
          </div>
        )}

        {/* Filters and Search - Only show when local is selected */}
        {documentSource === 'local' && (
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 md:gap-4 bg-black border border-gray-400 rounded-xl p-3 md:p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
          <div className="flex items-center gap-2">
            {/* Date Range */}
            <div className="relative">
              <select 
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value)}
                className="flex items-center gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-black border border-white rounded-lg text-white hover:bg-white/10 transition-colors text-xs md:text-sm appearance-none cursor-pointer pr-8"
              >
                <option value="Last 7 Days" className="bg-black">Last 7 Days</option>
                <option value="Last 30 Days" className="bg-black">Last 30 Days</option>
                <option value="Last 90 Days" className="bg-black">Last 90 Days</option>
                <option value="All Time" className="bg-black">All Time</option>
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white pointer-events-none" />
            </div>

            {/* Type Filter */}
            <div className="relative">
              <select 
                value={selectedFileType}
                onChange={(e) => setSelectedFileType(e.target.value)}
                className="flex items-center gap-2 px-2 md:px-4 py-1.5 md:py-2 bg-black border border-white rounded-lg text-white hover:bg-white/10 transition-colors text-xs md:text-sm appearance-none cursor-pointer pr-8"
              >
                <option value="all" className="bg-black">All Types</option>
                <option value="PDF" className="bg-black">PDF</option>
                <option value="DOC" className="bg-black">DOC/DOCX</option>
                <option value="XLS" className="bg-black">XLS/XLSX</option>
                <option value="IMG" className="bg-black">Images</option>
              </select>
              <ChevronDown size={14} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white pointer-events-none" />
            </div>
          </div>

          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
            <input
              type="text"
              placeholder="Search files"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 md:pl-10 pr-4 py-1.5 md:py-2 bg-black border border-white rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-cyan-500 transition-colors text-sm"
            />
          </div>
        </div>
        )}

        {/* Category Tabs - Only show when local is selected */}
        {documentSource === 'local' && (
        <div className="mt-3 md:mt-4 flex flex-wrap gap-1.5 md:gap-2 items-center">
          {categories.map((category) => (
            <button
              key={category.id}
              draggable={category.id !== 'all'}
              onDragStart={(e) => handleCategoryDragStart(e, category.id)}
              onDragOver={handleCategoryDragOver}
              onDrop={(e) => handleCategoryDrop(e, category.id)}
              onClick={() => setSelectedCategory(category.id)}
              data-testid={`document-category-${category.id}`}
              className={`px-2 md:px-4 py-1 md:py-2 rounded-lg font-medium transition-all flex items-center gap-1 md:gap-2 text-xs md:text-sm ${
                draggedCategoryId === category.id ? 'opacity-50' : ''
              } ${
                selectedCategory === category.id
                  ? `${category.color} ${category.textColor} shadow-lg`
                  : 'bg-black border border-white/40 text-white hover:bg-white/10'
              }`}
            >
              {/* Icon for the category */}
              <span>{category.icon}</span>
              <span>{category.name}</span>
              {selectedCategory === category.id && (
                <span className="bg-white/20 px-1.5 md:px-2 py-0.5 rounded-full text-[10px] md:text-xs">
                  {selectedCategory === 'all' 
                    ? totalCount 
                    : (currentFolder ? filteredFiles.length : filteredFolders.length + filteredFiles.length)
                  }
                </span>
              )}
            </button>
          ))}
          
          {/* Edit Categories Button */}
          <button
            onClick={() => setShowDocCategoryModal(true)}
            data-testid="edit-document-categories-btn"
            className="px-2 md:px-3 py-1 md:py-2 rounded-lg text-xs md:text-sm font-medium transition-all whitespace-nowrap bg-transparent border border-dashed border-gray-500 text-gray-400 hover:bg-gray-800 hover:text-white flex items-center gap-1"
          >
            <Pencil size={12} />
            <span className="hidden md:inline">Edit</span>
          </button>
        </div>
        )}
      </div>

      {/* Compact Folders Row - Only show when local is selected */}
      {documentSource === 'local' && !currentFolder && filteredFolders.length > 0 && (
        <div className="mb-3 md:mb-4">
          <div className="bg-black border border-gray-400 rounded-xl p-3 md:p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            <div className="flex items-center justify-between mb-3 md:mb-4">
              <h3 className="text-white font-semibold text-sm md:text-base">Folders ({filteredFolders.length})</h3>
              <button
                onClick={() => setShowAllFoldersModal(true)}
                className="text-xs md:text-sm text-gray-400 hover:text-white transition-colors"
              >
                View All
              </button>
            </div>
            <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2" style={{ scrollbarWidth: 'thin' }}>
              {filteredFolders.length > 0 ? (
                filteredFolders.map((folder) => (
                  <div
                    key={folder.id}
                    className={`bg-black border rounded-xl p-3 transition-all duration-300 cursor-pointer flex items-center gap-3 min-w-[180px] md:min-w-[220px] max-w-[180px] md:max-w-[220px] ${
                      dragOverFolderId === folder.id 
                        ? 'border-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.5)] scale-105 bg-cyan-900/20' 
                        : 'border-gray-400 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)]'
                    }`}
                    onClick={() => openFolder(folder)}
                    onDragOver={(e) => handleDragOver(e, folder.id)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => handleDrop(e, folder)}
                  >
                    {/* Folder Icon */}
                    <div className="w-8 md:w-10 h-8 md:h-10 flex items-center justify-center flex-shrink-0">
                      <FolderOpen className={`${dragOverFolderId === folder.id ? 'text-cyan-400' : getFolderIconColor(folder.color)} w-full h-full`} fill="currentColor" />
                    </div>
                    
                    {/* Folder Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-semibold text-sm md:text-base truncate mb-1">{folder.name}</h4>
                      <span className={`${folder.color || 'bg-gray-500'} text-white text-xs md:text-sm px-2 py-0.5 rounded-full inline-block`}>
                        {folder.category || getCategoryFromColor(folder.color)}
                      </span>
                    </div>
                    
                    {/* Drop indicator when dragging over */}
                    {dragOverFolderId === folder.id && (
                      <div className="absolute inset-0 flex items-center justify-center bg-cyan-500/10 rounded-xl pointer-events-none">
                        <span className="text-cyan-400 text-xs font-medium">Drop here</span>
                      </div>
                    )}
                    
                    {/* Delete Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFolder(folder.id);
                      }}
                      className="p-1.5 hover:bg-red-500/20 rounded transition-colors"
                    >
                      <Trash2 size={16} className="text-gray-400 hover:text-red-400" />
                    </button>
                  </div>
                ))
              ) : (
                <div className="text-gray-500 text-sm md:text-base py-4 px-2">
                  No folders yet. Click &quot;Add Folder&quot; to create one.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Files Section - Only show when local is selected */}
      {documentSource === 'local' && (
      <div>
        <div className="flex items-center justify-between mb-3 md:mb-4">
          <h2 className="text-base md:text-xl font-bold text-white">
            Files ({filteredFiles.length}) - View: {viewMode}
          </h2>
          
          {/* Selection Actions Bar - Appears when files are selected */}
          {selectedItems.length > 0 && (
            <div className="flex items-center gap-2 md:gap-3">
              <span className="text-cyan-400 text-xs md:text-sm font-medium">
                {selectedItems.length} selected
              </span>
              <button
                onClick={() => setShowMoveToFolderModal(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs md:text-sm rounded-lg transition-colors"
              >
                <FolderOpen size={14} />
                Move to Folder
              </button>
              <button
                onClick={() => setSelectedItems([])}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-xs md:text-sm rounded-lg transition-colors"
              >
                <X size={14} />
                Clear
              </button>
            </div>
          )}
        </div>
        
        {viewMode === 'grid' ? (
          // Grid View
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-4">
            {filteredFiles.length > 0 ? (
              filteredFiles.map((file) => {
                const { icon: Icon, color } = getFileIcon(file.type);
                
                return (
                  <div
                    key={file.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, file)}
                    className="bg-black border border-gray-400 rounded-xl p-4 hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300 cursor-pointer group relative"
                    onClick={() => openFile(file)}
                  >
                    {/* Selection Checkbox */}
                    <div className="absolute top-3 left-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(file.id)}
                        onChange={() => toggleSelection(file.id)}
                        className="w-4 h-4 rounded border-white bg-black checked:bg-cyan-500"
                      />
                    </div>

                    {/* More Options with Dropdown */}
                    <div className="absolute top-3 right-3" onClick={(e) => e.stopPropagation()}>
                      <button 
                        onClick={(e) => {
                          openDropdown(e, file);
                        }}
                        className="text-white hover:text-cyan-400 transition-colors"
                      >
                        <MoreVertical size={18} />
                      </button>
                    </div>

                    {/* File Icon - Color coordinated with category */}
                    <div className="flex flex-col items-center justify-center mt-4">
                      <div className={`w-16 h-20 ${file.color || getCategoryColor(file.category)} rounded-lg flex items-center justify-center mb-3`}>
                        <div className="text-white font-bold text-xs">
                          {file.type}
                        </div>
                      </div>
                      
                      {/* Category Tag */}
                      <span className={`${getCategoryColor(file.category)} text-white text-xs px-2 py-1 rounded-full mb-2`}>
                        {file.category}
                      </span>
                      
                      {/* File Name */}
                      <h3 className="text-white font-semibold text-center text-sm mb-2 line-clamp-2">
                        {file.name}
                      </h3>
                      
                      {/* File Details */}
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        <span>{file.size}</span>
                        <span>{file.date}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="col-span-full text-center py-16">
                <FolderOpen size={64} className="mx-auto text-gray-600 mb-4" />
                <div className="text-gray-400 text-xl mb-2">No documents yet</div>
                <p className="text-gray-500 mb-6">Upload your first document to get started</p>
                <button 
                  onClick={() => setShowAddDocumentModal(true)}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all font-semibold"
                >
                  <Plus size={18} className="inline mr-2" />
                  Add Document
                </button>
              </div>
            )}
          </div>
        ) : (
          // List View
          <div className="space-y-4">
            {/* Files Table */}
            <div className="bg-black border border-gray-400 rounded-xl overflow-hidden hover:border-gray-300 hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300">
            {/* Table Header - Hidden on mobile */}
            <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-black border-b border-white text-gray-400 text-sm font-semibold">
              <div className="col-span-4">Name</div>
              <div className="col-span-2">Last Activity</div>
              <div className="col-span-2">Category</div>
              <div className="col-span-2">Size</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            
            {/* Mobile Header */}
            <div className="md:hidden grid grid-cols-12 gap-2 px-3 py-2 bg-black border-b border-white text-gray-400 text-xs font-semibold">
              <div className="col-span-6">Name</div>
              <div className="col-span-3">Category</div>
              <div className="col-span-3 text-right">Actions</div>
            </div>
            
            {/* Table Rows */}
            {filteredFiles.length > 0 ? (
              filteredFiles
                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                .map((file, index) => {
                const { icon: Icon, color } = getFileIcon(file.type);
                
                return (
                  <div key={file.id}>
                    {/* Desktop Row */}
                    <div
                      draggable
                      onDragStart={(e) => handleDragStart(e, file)}
                      onClick={() => openFile(file)}
                      className={`hidden md:grid grid-cols-12 gap-4 px-6 py-4 hover:bg-white/5 transition-all cursor-pointer ${
                        index !== filteredFiles.length - 1 ? 'border-b border-white/20' : ''
                      }`}
                    >
                      {/* Checkbox + Name Column */}
                      <div className="col-span-4 flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(file.id)}
                          onChange={() => toggleSelection(file.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-white bg-black checked:bg-cyan-500 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-white font-semibold text-sm truncate">{file.name}</div>
                          <div className="text-gray-400 text-xs">Created: {file.date}</div>
                        </div>
                      </div>
                      
                      {/* Last Activity Column */}
                      <div className="col-span-2 flex items-center text-gray-300 text-sm">
                        {file.date}
                      </div>
                      
                      {/* Category Column */}
                      <div className="col-span-2 flex items-center">
                        <span className={`${getCategoryColor(file.category)} text-white text-xs px-3 py-1 rounded-full`}>
                          {file.category}
                        </span>
                      </div>
                      
                      {/* Size Column */}
                      <div className="col-span-2 flex items-center text-gray-300 text-sm">
                        {file.size}
                      </div>
                      
                      {/* Actions Column */}
                      <div className="col-span-2 flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openFile(file);
                          }}
                          className="px-3 py-1 bg-black border border-white text-white text-sm rounded hover:bg-white/10 transition-colors"
                        >
                          View
                        </button>
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDropdown(e, file);
                            }}
                            className="p-1 text-white hover:text-cyan-400 transition-colors"
                          >
                            <MoreVertical size={16} />
                          </button>
                        </div>
                      </div>
                    </div>
                    
                    {/* Mobile Row */}
                    <div
                      className={`md:hidden grid grid-cols-12 gap-2 px-3 py-3 hover:bg-white/5 transition-all cursor-pointer ${
                        index !== filteredFiles.length - 1 ? 'border-b border-white/20' : ''
                      }`}
                      onClick={() => openFile(file)}
                    >
                      {/* Checkbox + Name Column */}
                      <div className="col-span-6 flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(file.id)}
                          onChange={() => toggleSelection(file.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded border-white bg-black checked:bg-cyan-500 flex-shrink-0"
                        />
                        <div className="min-w-0">
                          <div className="text-white font-semibold text-xs truncate">{file.name}</div>
                          <div className="text-gray-400 text-[10px]">{file.size}</div>
                        </div>
                      </div>
                      
                      {/* Category Column */}
                      <div className="col-span-3 flex items-center">
                        <span className={`${getCategoryColor(file.category)} text-white text-[10px] px-2 py-0.5 rounded-full truncate`}>
                          {file.category}
                        </span>
                      </div>
                      
                      {/* Actions Column */}
                      <div className="col-span-3 flex items-center justify-end gap-1">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditDocument(file);
                          }}
                          className="p-1 text-cyan-400 hover:text-cyan-300"
                          title="Edit"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openFile(file);
                          }}
                          className="px-2 py-1 bg-black border border-white text-white text-[10px] rounded hover:bg-white/10 transition-colors"
                        >
                          View
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center py-8 md:py-12">
                <div className="text-gray-400 text-sm md:text-lg">No files found in this category</div>
                <button 
                  onClick={() => setSelectedCategory('all')}
                  className="mt-3 md:mt-4 px-3 md:px-4 py-1.5 md:py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  View All Files
                </button>
              </div>
            )}
            
            {/* Pagination */}
            {filteredFiles.length > 0 && (
              (() => {
                const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);
                const startIndex = (currentPage - 1) * itemsPerPage;
                const endIndex = Math.min(startIndex + itemsPerPage, filteredFiles.length);
                
                return (
                  <div className="flex items-center justify-between px-3 md:px-6 py-3 md:py-4 border-t border-white/20">
                    <div className="text-gray-400 text-xs md:text-sm">
                      Showing {startIndex + 1}-{endIndex} of {filteredFiles.length} Documents
                    </div>
                    <div className="flex items-center gap-1 md:gap-2">
                      <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className={`px-2 md:px-3 py-1 transition-colors text-xs md:text-sm ${
                          currentPage === 1 
                            ? 'text-gray-600 cursor-not-allowed' 
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        Prev
                      </button>
                      {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                        let pageNum;
                        if (totalPages <= 5) {
                          pageNum = i + 1;
                        } else if (currentPage <= 3) {
                          pageNum = i + 1;
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i;
                        } else {
                          pageNum = currentPage - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-2 md:px-3 py-1 rounded text-xs md:text-sm font-semibold transition-colors ${
                              currentPage === pageNum
                                ? 'bg-white text-black'
                                : 'text-gray-400 hover:text-white'
                            }`}
                          >
                            {pageNum}
                          </button>
                        );
                      })}
                      <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className={`px-2 md:px-3 py-1 transition-colors text-xs md:text-sm ${
                          currentPage === totalPages 
                            ? 'text-gray-600 cursor-not-allowed' 
                            : 'text-gray-400 hover:text-white'
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
          </div>
        )}
      </div>
      )}

      {/* View All Folders Modal */}
      {showAllFoldersModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-white rounded-xl max-w-5xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-black border-b-2 border-white p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">All Folders ({allFoldersDisplay.length})</h2>
              <button
                onClick={() => setShowAllFoldersModal(false)}
                className="text-white hover:text-cyan-400 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {allFoldersDisplay.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <FolderOpen size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No folders found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {allFoldersDisplay.map((folder) => (
                    <div
                      key={folder.id}
                      className="bg-black border border-white/40 rounded-lg p-4 hover:border-cyan-500 hover:shadow-[0_0_15px_rgba(6,182,212,0.3)] transition-all cursor-pointer relative group"
                    >
                      {/* Edit/Delete buttons - show on hover */}
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditFolderModal(folder);
                            setShowAllFoldersModal(false);
                          }}
                          className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded text-white"
                          title="Edit folder"
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteFolder(folder.id);
                          }}
                          className="p-1.5 bg-red-600 hover:bg-red-700 rounded text-white"
                          title="Delete folder"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      
                      <div 
                        className="flex flex-col items-center"
                        onClick={() => {
                          openFolder(folder);
                          setShowAllFoldersModal(false);
                        }}
                      >
                        <div className="w-16 h-16 flex items-center justify-center mb-3">
                          <FolderOpen className={`${getFolderIconColor(folder.color)} w-full h-full drop-shadow-lg`} fill="currentColor" />
                        </div>
                        <span className={`${folder.color || 'bg-gray-500'} text-white text-xs px-2 py-1 rounded-full mb-2`}>
                          {folder.category || getCategoryFromColor(folder.color)}
                        </span>
                        <h3 className="text-white font-semibold text-center text-sm mb-1 line-clamp-2">
                          {folder.name}
                        </h3>
                        <p className="text-gray-400 text-xs">{folder.size || '0 files'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* File Viewer Modal */}
      {showFileViewer && selectedFile && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-white rounded-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="bg-black border-b-2 border-white p-4 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-12 ${selectedFile.color || 'bg-blue-500'} rounded-lg flex items-center justify-center`}>
                  <div className="text-white font-bold text-xs">
                    {selectedFile.type || selectedFile.file_type || 'DOC'}
                  </div>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">{selectedFile.name || selectedFile.original_filename}</h2>
                  <p className="text-gray-400 text-sm">{selectedFile.size} • {selectedFile.date || (selectedFile.created_at ? new Date(selectedFile.created_at).toLocaleDateString() : '')}</p>
                </div>
                <span className={`${getCategoryColor(selectedFile.category)} text-white text-xs px-3 py-1 rounded-full`}>
                  {selectedFile.category || 'Document'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {(selectedFile.file_path || selectedFile.download_url) && (
                  <a 
                    href={selectedFile.file_path ? `${API_URL}${selectedFile.file_path}` : `${API_URL}${selectedFile.download_url}`}
                    download
                    className="px-4 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors text-sm font-semibold inline-flex items-center"
                  >
                    <Download size={16} className="mr-2" />
                    Download
                  </a>
                )}
                <button
                  onClick={() => {
                    setShowFileViewer(false);
                    setSelectedFile(null);
                  }}
                  className="text-white hover:text-cyan-400 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>
            
            {/* Document Preview */}
            <div className="flex-1 overflow-auto bg-gray-900">
              {/* Check if we have an actual file to display */}
              {(selectedFile.file_path || selectedFile.download_url) ? (
                <div className="w-full h-full min-h-[600px]">
                  {/* Get the file URL */}
                  {(() => {
                    const fileUrl = selectedFile.file_path 
                      ? `${API_URL}${selectedFile.file_path}` 
                      : `${API_URL}${selectedFile.download_url}`;
                    const fileType = (selectedFile.file_type || selectedFile.type || selectedFile.content_type || '').toLowerCase();
                    const fileName = (selectedFile.name || selectedFile.original_filename || '').toLowerCase();
                    
                    // Image Files - check both file_type and file extension
                    const isImage = fileType.includes('image') || fileType === 'img' ||
                        fileType.includes('png') || fileType.includes('jpg') || 
                        fileType.includes('jpeg') || fileType.includes('gif') ||
                        fileType.includes('webp') || fileType.includes('svg') ||
                        fileName.endsWith('.png') || fileName.endsWith('.jpg') ||
                        fileName.endsWith('.jpeg') || fileName.endsWith('.gif') ||
                        fileName.endsWith('.webp') || fileName.endsWith('.svg');
                    
                    if (isImage) {
                      return (
                        <div className="flex items-center justify-center p-8 min-h-[600px] bg-gray-900/50 rounded-lg">
                          <img
                            src={fileUrl}
                            alt={selectedFile.name || selectedFile.original_filename}
                            className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `
                                <div class="text-center text-white">
                                  <p class="text-lg mb-4">Unable to load image preview</p>
                                  <a href="${fileUrl}" target="_blank" rel="noopener noreferrer" class="px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg inline-block">
                                    Download Image
                                  </a>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      );
                    }
                    
                    // PDF Files
                    if (fileType.includes('pdf')) {
                      // Use Google Docs viewer as primary method (works everywhere)
                      const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`;
                      
                      return (
                        <div className="w-full h-full min-h-[600px] flex flex-col">
                          {/* Try native browser PDF rendering first */}
                          <iframe
                            src={fileUrl}
                            className="w-full flex-1 min-h-[600px] bg-white rounded-lg"
                            title={selectedFile.name || selectedFile.original_filename}
                            style={{ minHeight: '600px' }}
                            onError={() => {
                              // Fallback handled below
                            }}
                          />
                          {/* Fallback: Download link */}
                          <div className="mt-4 text-center">
                            <p className="text-gray-400 text-sm mb-2">Can't see the PDF?</p>
                            <a 
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm inline-flex items-center gap-2"
                            >
                              <Download size={16} />
                              Open PDF in New Tab
                            </a>
                          </div>
                        </div>
                      );
                    }
                    
                    // Text Files and unknown file types - show with iframe or fetch content
                    if (fileType.includes('text') || fileType.includes('txt') || fileType === 'txt' ||
                        !fileType || fileType === '' || fileType.includes('plain')) {
                      return (
                        <div className="flex flex-col h-full min-h-[600px]">
                          <iframe
                            src={fileUrl}
                            className="w-full h-full min-h-[600px] bg-white"
                            title={selectedFile.name || selectedFile.original_filename}
                          />
                        </div>
                      );
                    }
                    
                    // Word Documents - try to embed with Google Docs viewer
                    if (fileType.includes('doc') || fileType.includes('docx') || 
                        fileType === 'doc' || fileType === 'docx') {
                      return (
                        <div className="flex flex-col items-center justify-center p-8 min-h-[600px] text-center">
                          <div className={`w-24 h-28 bg-blue-600 rounded-xl flex items-center justify-center mb-6`}>
                            <span className="text-white font-bold text-xl">DOC</span>
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2">{selectedFile.name || selectedFile.original_filename}</h3>
                          <p className="text-gray-400 mb-6">Word Document</p>
                          <div className="flex gap-4">
                            <a 
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold inline-flex items-center gap-2"
                            >
                              <Eye size={20} />
                              Open in New Tab
                            </a>
                            <a 
                              href={fileUrl}
                              download
                              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold inline-flex items-center gap-2"
                            >
                              <Download size={20} />
                              Download
                            </a>
                          </div>
                        </div>
                      );
                    }
                    
                    // Excel Files
                    if (fileType.includes('xls') || fileType.includes('xlsx') || fileType.includes('excel') ||
                        fileType === 'xls' || fileType === 'xlsx') {
                      return (
                        <div className="flex flex-col items-center justify-center p-8 min-h-[600px] text-center">
                          <div className={`w-24 h-28 bg-green-600 rounded-xl flex items-center justify-center mb-6`}>
                            <span className="text-white font-bold text-xl">XLS</span>
                          </div>
                          <h3 className="text-xl font-bold text-white mb-2">{selectedFile.name || selectedFile.original_filename}</h3>
                          <p className="text-gray-400 mb-6">Excel Spreadsheet</p>
                          <div className="flex gap-4">
                            <a 
                              href={fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold inline-flex items-center gap-2"
                            >
                              <Eye size={20} />
                              Open in New Tab
                            </a>
                            <a 
                              href={fileUrl}
                              download
                              className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold inline-flex items-center gap-2"
                            >
                              <Download size={20} />
                              Download
                            </a>
                          </div>
                        </div>
                      );
                    }
                    
                    // Other file types - show download/open options
                    return (
                      <div className="flex flex-col items-center justify-center p-8 min-h-[600px] text-center">
                        <div className={`w-24 h-28 ${selectedFile.color || 'bg-blue-500'} rounded-xl flex items-center justify-center mb-6`}>
                          <span className="text-white font-bold text-2xl">
                            {selectedFile.type || selectedFile.file_type || 'DOC'}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-white mb-2">{selectedFile.name || selectedFile.original_filename}</h3>
                        <p className="text-gray-400 mb-6">Preview not available for this file type</p>
                        <div className="flex gap-4">
                          <a 
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold inline-flex items-center gap-2"
                          >
                            <Eye size={20} />
                            Open in New Tab
                          </a>
                          <a 
                            href={fileUrl}
                            download
                            className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-semibold inline-flex items-center gap-2"
                          >
                            <Download size={20} />
                            Download
                          </a>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                /* Fallback for documents without file_path (mock data) */
                <div className="p-6">
                  <div className="bg-white rounded-lg p-8 min-h-[600px] shadow-2xl">
                    <div className="max-w-4xl mx-auto">
                      <h1 className="text-3xl font-bold text-black mb-4">{(selectedFile.name || '').replace(/_/g, ' ')}</h1>
                      <div className="border-b-2 border-gray-300 mb-6"></div>
                      
                      <div className="space-y-4 text-black">
                        <p className="text-lg leading-relaxed">
                          This is a preview of <strong>{selectedFile.name}</strong>. 
                        </p>
                        
                        <h2 className="text-2xl font-bold mt-6 mb-3">Document Details</h2>
                        <ul className="list-disc list-inside space-y-2">
                          <li>File Type: {selectedFile.type || selectedFile.file_type}</li>
                          <li>Category: {selectedFile.category}</li>
                          <li>Size: {selectedFile.size}</li>
                          <li>Last Modified: {selectedFile.date}</li>
                        </ul>

                        <div className="mt-8 p-4 bg-gray-100 rounded-lg">
                          <p className="text-gray-600 text-sm">
                            This document does not have a stored file. Upload a file to view its actual content.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Context Menu (shown on right-click or click more options) */}
      {/* This would be implemented with a proper dropdown/modal */}

      {/* Add Document Modal */}
      {showAddDocumentModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowAddDocumentModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Plus className="text-blue-400" size={24} />
                Add New Document
              </h2>
              <button 
                onClick={() => {
                  setShowAddDocumentModal(false);
                  setSelectedUploadFile(null);
                  setNewDocument({ name: '', category: 'Properties', type: 'PDF' });
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Upload Area */}
            <div 
              className="border-2 border-dashed border-gray-600 rounded-xl p-8 mb-6 text-center hover:border-blue-500 transition-colors cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg,.gif,.txt,.csv"
              />
              {selectedUploadFile ? (
                <>
                  <FileText className="mx-auto text-green-400 mb-3" size={48} />
                  <p className="text-white font-semibold mb-1">{selectedUploadFile.name}</p>
                  <p className="text-gray-400 text-sm mb-3">
                    {(selectedUploadFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button 
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUploadFile(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                  >
                    Change File
                  </button>
                </>
              ) : (
                <>
                  <Upload className="mx-auto text-gray-400 mb-3" size={48} />
                  <p className="text-white font-semibold mb-1">Drag and drop files here</p>
                  <p className="text-gray-400 text-sm mb-3">or click to browse</p>
                  <button className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
                    Browse Files
                  </button>
                </>
              )}
            </div>

            {/* Document Details */}
            <div className="space-y-4">
              {/* Document Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Document Name *</label>
                <input
                  type="text"
                  value={newDocument.name}
                  onChange={(e) => setNewDocument({ ...newDocument, name: e.target.value })}
                  placeholder="Enter document name"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={newDocument.category}
                  onChange={(e) => setNewDocument({ ...newDocument, category: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-blue-500 transition-colors"
                >
                  {customDocCategories
                    .filter(cat => cat.id !== 'all')
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))
                  }
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddDocumentModal(false);
                  setSelectedUploadFile(null);
                  setNewDocument({ name: '', category: 'Properties', type: 'PDF' });
                }}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleUploadDocument}
                disabled={isUploading || !selectedUploadFile || !newDocument.name}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg transition-all font-semibold flex items-center gap-2"
              >
                {isUploading ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Uploading...
                  </>
                ) : (
                  'Upload Document'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Document Modal */}
      {showEditDocumentModal && editingDocument && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowEditDocumentModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <Edit2 className="text-cyan-400" size={24} />
                Edit Document Details
              </h2>
              <button 
                onClick={() => {
                  setShowEditDocumentModal(false);
                  setEditingDocument(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Document Preview */}
            <div className="bg-gray-800 rounded-xl p-4 mb-6 flex items-center gap-4">
              <div className="p-3 bg-gray-700 rounded-lg">
                <FileText className="text-cyan-400" size={32} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white font-semibold truncate">{editingDocument.filename || editingDocument.name}</div>
                <div className="text-gray-400 text-sm">{editingDocument.size}</div>
              </div>
            </div>

            {/* Edit Fields */}
            <div className="space-y-4">
              {/* Document Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Display Name</label>
                <input
                  type="text"
                  value={editDocumentName}
                  onChange={(e) => setEditDocumentName(e.target.value)}
                  placeholder="Enter document name"
                  className="w-full px-4 py-3 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                  data-testid="edit-document-name-input"
                />
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Category</label>
                <select
                  value={editDocumentCategory}
                  onChange={(e) => setEditDocumentCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500"
                  data-testid="edit-document-category-select"
                >
                  {customDocCategories
                    .filter(cat => cat.id !== 'all')
                    .map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))
                  }
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Notes</label>
                <textarea
                  value={editDocumentNotes}
                  onChange={(e) => setEditDocumentNotes(e.target.value)}
                  placeholder="Add notes about this document..."
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-800 text-white border border-gray-600 rounded-lg focus:outline-none focus:border-cyan-500 resize-none"
                  data-testid="edit-document-notes-input"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowEditDocumentModal(false);
                  setEditingDocument(null);
                }}
                className="flex-1 px-6 py-2.5 border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveDocumentEdit}
                className="flex-1 px-6 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white rounded-lg transition-all font-semibold"
                data-testid="save-document-edit-btn"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Folder Modal */}
      {showAddFolderModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowAddFolderModal(false)}>
          <div className="bg-black border-2 border-gray-400 rounded-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                <FolderPlus className="text-blue-400" size={24} />
                Create New Folder
              </h2>
              <button 
                onClick={() => {
                  setShowAddFolderModal(false);
                  setNewFolderName('');
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Folder Details */}
            <div className="space-y-4">
              {/* Folder Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Folder Name *</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-600 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 transition-colors"
                  autoFocus
                />
              </div>

              {/* Folder Color */}
              <div>
                <label className="block text-sm font-semibold text-gray-400 mb-2">Folder Color</label>
                <div className="flex gap-2 flex-wrap">
                  {[
                    { color: 'bg-blue-500', name: 'Blue' },
                    { color: 'bg-purple-500', name: 'Purple' },
                    { color: 'bg-green-500', name: 'Green' },
                    { color: 'bg-yellow-500', name: 'Yellow' },
                    { color: 'bg-red-500', name: 'Red' },
                    { color: 'bg-pink-500', name: 'Pink' },
                    { color: 'bg-cyan-500', name: 'Cyan' },
                    { color: 'bg-orange-500', name: 'Orange' },
                  ].map((c) => (
                    <button
                      key={c.color}
                      onClick={() => setNewFolderColor(c.color)}
                      className={`w-10 h-10 rounded-lg ${c.color} transition-all ${
                        newFolderColor === c.color ? 'ring-2 ring-white ring-offset-2 ring-offset-black scale-110' : 'hover:scale-105'
                      }`}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddFolderModal(false);
                  setNewFolderName('');
                }}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                disabled={isCreatingFolder}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={isCreatingFolder || !newFolderName.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg transition-all font-semibold flex items-center gap-2"
              >
                {isCreatingFolder ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Folder'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Folder Modal */}
      {showEditFolderModal && editingFolder && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-white/20">
              <h2 className="text-xl font-bold text-white">Edit Folder</h2>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Folder Name */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Folder Name</label>
                <input
                  type="text"
                  value={editFolderName}
                  onChange={(e) => setEditFolderName(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-cyan-500"
                  placeholder="Enter folder name"
                />
              </div>
              
              {/* Folder Color & Category */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Color & Category</label>
                <div className="grid grid-cols-4 gap-2">
                  {folderColorOptions.map((option) => (
                    <button
                      key={option.color}
                      onClick={() => {
                        setEditFolderColor(option.color);
                        setEditFolderCategory(option.category);
                      }}
                      className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                        editFolderColor === option.color
                          ? 'border-cyan-500 bg-cyan-500/10'
                          : 'border-white/20 hover:border-white/40'
                      }`}
                    >
                      <FolderOpen className={`${getFolderIconColor(option.color)} w-8 h-8 mb-1`} fill="currentColor" />
                      <span className="text-xs text-gray-400">{option.category}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Preview */}
              <div className="bg-white/5 rounded-lg p-4 flex items-center gap-4">
                <FolderOpen className={`${getFolderIconColor(editFolderColor)} w-12 h-12`} fill="currentColor" />
                <div>
                  <p className="text-white font-semibold">{editFolderName || 'Folder Name'}</p>
                  <span className={`${editFolderColor} text-white text-xs px-2 py-0.5 rounded-full`}>
                    {editFolderCategory}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="p-6 border-t border-white/20 flex items-center justify-end gap-3">
              <button
                onClick={() => {
                  setShowEditFolderModal(false);
                  setEditingFolder(null);
                }}
                className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateFolder}
                disabled={!editFolderName.trim()}
                className="px-6 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-600 text-white rounded-lg transition-all font-semibold"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move to Folder Modal */}
      {showMoveToFolderModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-black border-b border-white/20 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Move to Folder</h2>
                <p className="text-gray-400 text-sm mt-1">{selectedItems.length} file(s) selected</p>
              </div>
              <button
                onClick={() => setShowMoveToFolderModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            <div className="p-6">
              {/* Option to move to root (no folder) */}
              <button
                onClick={() => handleMoveFilesToFolder(null)}
                className="w-full mb-4 p-4 bg-gray-800 hover:bg-gray-700 border border-gray-600 rounded-lg flex items-center gap-4 transition-colors"
              >
                <Home className="text-gray-400 w-8 h-8" />
                <div className="text-left">
                  <p className="text-white font-semibold">Root (No Folder)</p>
                  <p className="text-gray-400 text-sm">Move files to the main documents area</p>
                </div>
              </button>
              
              {/* List of folders */}
              {allFoldersDisplay.length > 0 ? (
                <div className="space-y-2">
                  <p className="text-gray-400 text-sm mb-3">Or select a folder:</p>
                  {allFoldersDisplay.map((folder) => (
                    <button
                      key={folder.id}
                      onClick={() => handleMoveFilesToFolder(folder.id)}
                      className="w-full p-4 bg-gray-800/50 hover:bg-gray-700 border border-gray-600 hover:border-cyan-500 rounded-lg flex items-center gap-4 transition-all"
                    >
                      <FolderOpen className={`${getFolderIconColor(folder.color)} w-8 h-8`} fill="currentColor" />
                      <div className="text-left flex-1">
                        <p className="text-white font-semibold">{folder.name}</p>
                        <span className={`${folder.color || 'bg-gray-500'} text-white text-xs px-2 py-0.5 rounded-full`}>
                          {folder.category || getCategoryFromColor(folder.color)}
                        </span>
                      </div>
                      <ChevronRight className="text-gray-500" size={20} />
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <FolderOpen size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No folders available</p>
                  <p className="text-sm">Create a folder first to organize your files</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add to Meeting Modal */}
      {showAddToMeetingModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-black border-2 border-white rounded-xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="sticky top-0 bg-black border-b border-white/20 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">Add to Meeting</h2>
                <p className="text-gray-400 text-sm mt-1 truncate max-w-[300px]">
                  {documentToAddToMeeting?.name}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowAddToMeetingModal(false);
                  setDocumentToAddToMeeting(null);
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Search */}
            <div className="p-4 border-b border-white/10">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search meetings..."
                  value={meetingSearchQuery}
                  onChange={(e) => setMeetingSearchQuery(e.target.value)}
                  className="w-full bg-white/5 border border-white/20 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-cyan-500"
                />
              </div>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              {isLoadingMeetings ? (
                <div className="text-center py-8 text-gray-400">
                  <Loader2 className="animate-spin mx-auto mb-2" size={32} />
                  <p>Loading meetings...</p>
                </div>
              ) : filteredMeetings.length > 0 ? (
                <div className="space-y-2">
                  {filteredMeetings.map(meeting => (
                    <button
                      key={meeting.id}
                      onClick={() => handleAddDocumentToMeeting(meeting)}
                      disabled={isAddingToMeeting}
                      className="w-full p-4 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 rounded-lg flex items-start gap-4 transition-all text-left disabled:opacity-50"
                    >
                      <div className="bg-cyan-500/20 p-2 rounded-lg">
                        <Calendar className="text-cyan-400" size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-medium truncate">
                          {meeting.title || meeting.organization || 'Untitled Meeting'}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {meeting.date} {meeting.time && `at ${meeting.time}`}
                        </p>
                        {meeting.participants && meeting.participants.length > 0 && (
                          <p className="text-gray-500 text-xs mt-1 truncate">
                            With: {meeting.participants.map(p => p.name).join(', ')}
                          </p>
                        )}
                        {meeting.documents && meeting.documents.length > 0 && (
                          <span className="inline-flex items-center gap-1 mt-2 text-xs bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded">
                            <FileText size={10} />
                            {meeting.documents.length} doc(s)
                          </span>
                        )}
                      </div>
                      {isAddingToMeeting && (
                        <Loader2 className="animate-spin text-cyan-400" size={20} />
                      )}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <Calendar size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No meetings found</p>
                  <p className="text-sm">Create a meeting first to attach documents</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document Category Management Modal */}
      {showDocCategoryModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowDocCategoryModal(false)}>
          <div className="bg-gray-900 rounded-2xl w-full max-w-md border border-gray-700 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Edit Document Categories</h3>
              <button onClick={() => setShowDocCategoryModal(false)} className="p-2 hover:bg-gray-800 rounded-lg"><X size={18} className="text-gray-400" /></button>
            </div>
            <div className="p-4 border-b border-gray-700">
              <div className="flex gap-2 mb-3">
                <div className="relative">
                  <button className="w-10 h-10 bg-black border border-gray-600 rounded-lg text-xl flex items-center justify-center" onClick={(e) => e.currentTarget.nextElementSibling.classList.toggle('hidden')}>{newDocCategoryIcon}</button>
                  <div className="hidden absolute top-full left-0 mt-1 bg-gray-800 border border-gray-600 rounded-lg p-2 z-10 grid grid-cols-5 gap-1 w-40">
                    {docCategoryIcons.map(icon => (<button key={icon} onClick={(e) => { setNewDocCategoryIcon(icon); e.currentTarget.parentElement.classList.add('hidden'); }} className="w-7 h-7 hover:bg-gray-700 rounded text-lg flex items-center justify-center">{icon}</button>))}
                  </div>
                </div>
                <input type="text" value={newDocCategoryName} onChange={(e) => setNewDocCategoryName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addDocCategory()} placeholder="New category name..." className="flex-1 px-3 py-2 bg-black border border-gray-600 rounded-lg text-white text-sm" data-testid="new-document-category-input" />
              </div>
              <div className="flex gap-2 items-center mb-3">
                <span className="text-xs text-gray-400">Color:</span>
                <div className="flex gap-1 flex-wrap">
                  {docCategoryColors.map(color => (<button key={color} onClick={() => setNewDocCategoryColor(color)} className={`w-6 h-6 rounded-full ${color} ${newDocCategoryColor === color ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-900' : ''}`} />))}
                </div>
              </div>
              <button onClick={addDocCategory} disabled={!newDocCategoryName.trim()} className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1.5" data-testid="add-document-category-btn"><Plus size={14} />Add Category</button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto space-y-2">
              {customDocCategories.filter(c => c.id !== 'all').map(cat => (
                <div key={cat.id} className="flex items-center gap-2 p-2 bg-gray-800/50 rounded-lg group">
                  {editingDocCategory === cat.id ? (
                    <>
                      <span className="text-lg">{cat.icon}</span>
                      <input type="text" value={editDocCategoryValue} onChange={(e) => setEditDocCategoryValue(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') updateDocCategory(cat.id, editDocCategoryValue); if (e.key === 'Escape') { setEditingDocCategory(null); setEditDocCategoryValue(''); }}} className="flex-1 px-2 py-1 bg-black border border-gray-600 rounded text-white text-sm" autoFocus />
                      <button onClick={() => updateDocCategory(cat.id, editDocCategoryValue)} className="p-1.5 text-green-400 hover:bg-green-500/20 rounded"><Check size={14} /></button>
                      <button onClick={() => { setEditingDocCategory(null); setEditDocCategoryValue(''); }} className="p-1.5 text-red-400 hover:bg-red-500/20 rounded"><X size={14} /></button>
                    </>
                  ) : (
                    <>
                      <span className={`w-8 h-8 rounded-lg ${cat.color} flex items-center justify-center text-sm`}>{cat.icon}</span>
                      <span className="flex-1 text-white text-sm">{cat.name}{['Properties', 'Legal'].includes(cat.id) && <span className="ml-2 text-xs text-gray-500">(default)</span>}</span>
                      <button onClick={() => { setEditingDocCategory(cat.id); setEditDocCategoryValue(cat.name); }} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded opacity-0 group-hover:opacity-100"><Pencil size={14} /></button>
                      {!['Properties', 'Legal'].includes(cat.id) && <button onClick={() => deleteDocCategory(cat.id)} className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-500/20 rounded opacity-0 group-hover:opacity-100"><Trash2 size={14} /></button>}
                    </>
                  )}
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-gray-700">
              <button onClick={() => setShowDocCategoryModal(false)} className="w-full px-4 py-2.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm font-medium" data-testid="done-document-category-btn">Done</button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed-position dropdown - renders outside all overflow-hidden containers */}
      {openDropdownId && dropdownFile && (
        <div
          className="fixed bg-gray-900 border border-gray-600 rounded-lg shadow-2xl z-[9999] min-w-[170px] py-1"
          style={{ top: dropdownPos.top, right: dropdownPos.right }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => { openFile(dropdownFile); setOpenDropdownId(null); }}
            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
            data-testid="dropdown-view"
          >
            <Eye size={14} /> View
          </button>
          <button
            onClick={() => { handleEditDocument(dropdownFile); setOpenDropdownId(null); }}
            className="w-full px-4 py-2.5 text-left text-sm text-cyan-400 hover:bg-gray-700 flex items-center gap-2"
            data-testid={`edit-document-${dropdownFile.id}`}
          >
            <Edit2 size={14} /> Edit Details
          </button>
          <button
            onClick={() => { openAddToMeetingModal(dropdownFile); setOpenDropdownId(null); }}
            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2"
            data-testid="dropdown-add-meeting"
          >
            <Calendar size={14} /> Add to Meeting
          </button>
          {(dropdownFile.file_path || dropdownFile.download_url) && (
            <a
              href={dropdownFile.file_path ? `${API_URL}${dropdownFile.file_path}` : `${API_URL}${dropdownFile.download_url}`}
              download
              onClick={() => setOpenDropdownId(null)}
              className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-gray-700 flex items-center gap-2 block"
              data-testid="dropdown-download"
            >
              <Download size={14} /> Download
            </a>
          )}
          <button
            onClick={() => { handleDeleteDocument(dropdownFile.id); setOpenDropdownId(null); }}
            className="w-full px-4 py-2.5 text-left text-sm text-red-400 hover:bg-gray-700 flex items-center gap-2"
            data-testid="dropdown-delete"
          >
            <Trash2 size={14} /> Delete
          </button>
        </div>
      )}
    </div>
  );
};

export default RealEstateManagingPartnerDocuments;
